import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { StartBankIdAuthArgs } from '@organisms/BankIdLoginFlow/bankIdTypes';
import Text from '@atoms/Text/Text';
import Button from '@atoms/Button/Button';
import { trackBankIdLoginCancelled } from '@helpers/analyticsHelpers/trackLogin';
import useResponsive from '@hooks/useResponsive';
import { useFullScreenHandle } from 'react-full-screen';
import {
  InfoContainer,
  QrContainer,
  Wrapper,
  StyledClearIntervalsButton,
  StyledFullscreen,
  StyledBankIdImage,
} from './BankIdQr.styles';

export interface BankIdQrProps {
  startBankIdFlow: (args: StartBankIdAuthArgs) => void;
  handleCancel: () => void;
  showPending: boolean;
  showError: boolean;
  qrData: string;
  displayTimeLeft?: boolean;
  handleReset?: () => void;
}

/**
 * @description Note: manage mounts/unmounts properly for this component since unexpected unmounts/remounts will
 * cause the auth request flow to start over.
 */

const BankIdQr = ({
  startBankIdFlow,
  handleCancel,
  showPending,
  showError,
  qrData,
  displayTimeLeft,
  handleReset,
}: BankIdQrProps) => {
  const t = useTranslations('login');
  const [timeLeft, setTimeLeft] = useState<number>(30);
  const { fromTabletPortrait } = useResponsive();
  const handle = useFullScreenHandle();

  const qrButtonProps = fromTabletPortrait
    ? {
        role: 'button',
        'aria-label': t('bankId.qrCode'),
        tabIndex: 0,
        onClick: () => (handle.active ? handle.exit() : handle.enter()),
      }
    : {};

  const resetTimer = () => {
    setTimeLeft(30);
  };

  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    startBankIdFlow({ mobile: true, sameDevice: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (displayTimeLeft) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer); // Cleanup on unmount
    }
  }, [displayTimeLeft]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === ' ') {
        event.preventDefault(); // Prevent scrolling
        if (handleReset) handleReset();
        resetTimer();
      }
      if (event.key === 'Escape' && handle.active) {
        handle.exit();
      }
    };

    if (timeLeft > 0 && displayTimeLeft) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [timeLeft, handleReset, displayTimeLeft, handle]);

  if (showPending || showError) return null;

  const onClickHandler = () => {
    if (handle.active) {
      handle.exit();
    } else {
      trackBankIdLoginCancelled('QR');
      handleCancel();
    }
  };

  return (
    <StyledFullscreen handle={handle} $fullScreen={handle.active}>
      <Wrapper data-testid="bankid-qr" $fullScreen={handle.active}>
        {handle.active && (
          <StyledBankIdImage unoptimized src="/icons/logos/BankID.svg" alt="BankID" width={90} height={86} />
        )}

        <InfoContainer>
          <Text type="body">{t('bankId.startMobileAppQr')}</Text>
        </InfoContainer>
        <QrContainer {...qrButtonProps}>{qrData && <QRCodeSVG value={qrData} size={140} />}</QrContainer>

        {displayTimeLeft && (
          <>
            {/* Visually updates every second */}
            <Text type="body" color="red">
              {t('bankId.timeLeft', { seconds: timeLeft })}
            </Text>

            {/* Screen reader announces every 10 seconds */}
            <span
              style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
              aria-live="polite"
              aria-atomic="true"
            >
              {[25, 15, 5].includes(timeLeft) ? t('bankId.timeLeft', { seconds: timeLeft }) : ''}
            </span>

            <StyledClearIntervalsButton
              variant="primary"
              data-testid="clear-intervals-button"
              onClick={() => {
                if (handleReset) handleReset();
                resetTimer();
              }}
            >
              {t('bankId.clearIntervals')}
            </StyledClearIntervalsButton>
          </>
        )}

        <Button variant="link" size="small" onClick={onClickHandler}>
          {handle.active ? t('bankId.leaveFullscreen') : t('bankId.cancel')}
        </Button>
      </Wrapper>
    </StyledFullscreen>
  );
};

export default BankIdQr;
